import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormArray, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { isExecutionResult, isHttpResponseError } from "@tylertech/core-module";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { ClassMetadata, IMassUpdateCriteria, IMassUpdateProperty, PropertyMetadata, } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MassUpdateService } from "../../massUpdateService.g";

export interface MassUpdateDialogData
{
    readonly entityIds: string[];
    readonly localUpdate: boolean;
    readonly providerId: string;
}

@Component({
    selector: "mass-update-dialog-component",
    templateUrl: "./massUpdateDialog.tmpl.html",
    styleUrls: [
        "./massUpdateDialog.component.scss"
    ],
    standalone: false
})
export class MassUpdateDialogComponent implements OnDestroy, OnInit
{
    private readonly destructor$ = new Subject<void>();
    protected updatePromise?: Promise<void>;
    readonly formGroup: FormGroup;

    isLoaded: boolean;
    metadata: ClassMetadata;
    updateProperties: Map<string, IMassUpdateProperty>;

    constructor(
        private readonly dialogRef: MatDialogRef<MassUpdateDialogComponent, IMassUpdateCriteria | boolean | undefined>,
        @Inject(MAT_DIALOG_DATA) private readonly data: MassUpdateDialogData,
        private readonly $commonDialogService: CommonDialogsService,
        private $massUpdateService: MassUpdateService)
    {
        this.formGroup = new FormGroup({
            "properties": new FormArray([])
        });
    }

    private get providerId(): string { return this.data.providerId; }
    private get entityIds(): string[] { return this.data.entityIds; }
    private get localUpdate(): boolean { return this.data.localUpdate; }
    protected get propertiesControlArray(): FormArray { return this.formGroup.get("properties") as FormArray; }

    get CanUpdate(): boolean
    {
        return this.updateProperties
            && [...this.updateProperties.values()]
                .some(x => x.Update);
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        this.$massUpdateService.MassUpdateProperties(this.providerId)
            .then(result =>
            {
                this.metadata = result;
                this.updateProperties = new Map<string, IMassUpdateProperty>(
                    this.metadata.Properties.map(
                        prop =>
                        {
                            const massUpdateProperty: IMassUpdateProperty = {
                                PropertyLabel: prop.Label || prop.Name,
                                PropertyName: prop.Name,
                                PropertyValue: undefined,
                                Update: false,
                            };

                            const control = LgdFormControlFactory.checkbox();
                            this.propertiesControlArray.push(control);

                            subscribeToValueChanges(control, (newValue: boolean) =>
                            {
                                massUpdateProperty.Update = newValue;
                            });

                            return [
                                prop.Name,
                                massUpdateProperty
                            ];
                        }));
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        /**
         * Simple function to wrap subscriptions in standard pattern that will automatically unsubscribe when this component is destroyed.
         * @param control Control to listen to.
         * @param subscriptionFn Action to take when control's value changes.
         */
        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(
                takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    canEdit(property: PropertyMetadata): boolean
    {
        return this.updateProperties.get(property.Name)?.Update;
    }

    onCloseClick(): void
    {
        this.dialogRef.close();
    }

    onUpdateClick(): void
    {
        this.isLoaded = false;
        this.metadata.Properties.forEach(prop =>
        {
            const updateProperty = this.updateProperties.get(prop.Name);
            if (!updateProperty)
            {
                return;
            }

            updateProperty.PropertyValue = prop[prop.Name];
        });

        const criteria: IMassUpdateCriteria = {
            ProviderId: this.providerId,
            EntityIds: this.entityIds,
            Properties: [...this.updateProperties.values()],
            Id: undefined
        };

        if (this.localUpdate)
        {
            this.dialogRef.close(criteria);
            return;
        }

        this.updatePromise = this.$massUpdateService.MassUpdate(criteria)
            .then(
                result =>
                {
                    if (result)
                    {
                        this.dialogRef.close(true);
                    }
                },
                (result: unknown) =>
                {
                    let msg = "";
                    if (typeof result === "string")
                    {
                        msg = result;
                    }
                    else if (isExecutionResult(result) && Array.isArray(result.Result) && result.Result.length)
                    {
                        for (let i = 0, l = result.Result.length; i < l; i++)
                        {
                            msg += result.Result[i] + "<br/>";
                        }
                    }
                    else if (isHttpResponseError(result))
                    {
                        msg = result.error.Message;
                    }
                    else
                    {
                        msg = "An Error Occurred.";
                    }

                    return this.$commonDialogService.showAlertHtmlMessage(
                        "Failed to save due to the following: <br/>" + msg, "Save Failed!");
                }
            )
            .finally(() =>
            {
                this.updatePromise = undefined;
                this.isLoaded = true;
            });
    }
}
